import {Upload} from 'tus-js-client';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {UploadStrategy, UploadStrategyConfig} from './upload-strategy';
import {FileEntry} from '../../file-entry';
import {getAxiosErrorMessage} from '@common/http/get-axios-error-message';
import {apiClient} from '@common/http/query-client';
import {getCookie} from 'react-use-cookie';
import {CryptoService} from '@app/drive/encryption/crypto-service';
import {useEncryptionStore} from '@app/drive/encryption/encryption-store';

export class TusUpload implements UploadStrategy {
  constructor(private upload: Upload) {}

  start() {
    this.upload.start();
  }

  abort() {
    return this.upload.abort(true);
  }

  static async create(
    file: UploadedFile,
    {
      onProgress,
      onSuccess,
      onError,
      metadata,
      chunkSize,
      baseUrl,
    }: UploadStrategyConfig,
  ): Promise<TusUpload> {
    const tusFingerprint = ['tus', file.fingerprint, 'drive'].join('-');

    // Handle file encryption if enabled
    let fileToUpload = file.native;
    let encryptionMetadata: Record<string, string> = {};

    const encryption = useEncryptionStore.getState();
    if (encryption.isEncryptionEnabled && file.meta?.isEncrypted) {
      try {
        // Only encrypt if public key is available
        if (!encryption.publicKey) {
          console.warn('Encryption enabled but public key not available, uploading unencrypted');
        } else {
          const encryptionData = await encryptFileForUpload(file.native);
          fileToUpload = encryptionData.encryptedFile;
          encryptionMetadata = {
            isEncrypted: 'true',
            encryptedFileKey: encryptionData.encryptedKey,
            fileKeyIv: encryptionData.keyIv,
          };
        }
      } catch (err) {
        console.error('Failed to encrypt file:', err);
        // Don't fail the upload - continue with unencrypted upload
        console.warn('Falling back to unencrypted upload');
        fileToUpload = file.native;
        encryptionMetadata = {};
      }
    }

    const upload = new Upload(fileToUpload, {
      fingerprint: () => Promise.resolve(tusFingerprint),
      removeFingerprintOnSuccess: true,
      endpoint: `${baseUrl}/api/v1/tus/upload`,
      chunkSize,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      overridePatchMethod: true,
      metadata: {
        name: window.btoa(file.id),
        clientName: file.name,
        clientExtension: file.extension,
        clientMime: file.mime || '',
        clientSize: `${file.size}`,
        ...encryptionMetadata,
        ...(metadata as Record<string, string>),
      },
      headers: {
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      onError: err => {
        if ('originalResponse' in err && err.originalResponse) {
          try {
            const message = JSON.parse(err.originalResponse.getBody())?.message;
            onError?.(message, file);
          } catch (e) {
            onError?.(null, file);
          }
        } else {
          onError?.(null, file);
        }
      },
      onProgress(bytesUploaded, bytesTotal) {
        onProgress?.({bytesUploaded, bytesTotal});
      },
      onSuccess: async () => {
        const uploadKey = upload.url?.split('/').pop();
        try {
          if (uploadKey) {
            const response = await createFileEntry(uploadKey);
            onSuccess?.(response.fileEntry, file);
          }
        } catch (err) {
          localStorage.removeItem(tusFingerprint);
          onError?.(getAxiosErrorMessage(err), file);
        }
      },
    });

    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads.length) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    return new TusUpload(upload);
  }
}

async function encryptFileForUpload(file: File): Promise<{
  encryptedFile: File;
  encryptedKey: string;
  keyIv: string;
}> {
  // Generate file encryption key
  const fileKey = await CryptoService.generateFileKey();

  // Encrypt the file
  const { encryptedData, iv } = await CryptoService.encryptFile(
    new Uint8Array(await file.arrayBuffer()),
    fileKey
  );

  // Create new encrypted blob
  const encryptedBlob = new Blob([encryptedData], { type: 'application/octet-stream' });

  // Create new File with encrypted blob
  const encryptedFile = new File(
    [encryptedBlob],
    file.name + '.encrypted',
    { type: 'application/octet-stream' }
  );

  // Encrypt the file key with user's RSA public key
  const encryption = useEncryptionStore.getState();
  if (!encryption.publicKey) {
    throw new Error('User public key not available for file key encryption');
  }
  // Import the PEM-encoded public key to CryptoKey
  const publicKeyCryptoKey = await CryptoService.importPemKey(encryption.publicKey, 'public');
  const encryptedFileKey = await CryptoService.encryptFileKey(fileKey, publicKeyCryptoKey);

  return {
    encryptedFile,
    encryptedKey: encryptedFileKey,
    keyIv: iv,
  };
}

function createFileEntry(uploadKey: string): Promise<{fileEntry: FileEntry}> {
  return apiClient.post('tus/entries', {uploadKey}).then(r => r.data);
}
